import React from 'react';
import { Home, Package, Briefcase, Wallet, MessageSquare, Globe, User } from 'lucide-react';
import { DIRECTORY_ENABLED } from '../config/features';

/**
 * DoneDeal-style navigation for the Driver Dashboard.
 *  - Mobile  (<md): sticky bottom tab bar (5 icons + labels)
 *  - Desktop (≥md): horizontal pill bar at the top of the dashboard
 *
 * Props:
 *   active           — current view key
 *   onChange(key)    — fires when user taps a tab
 *   counts           — { jobs, work, messages } shown as small pill badges
 *
 * Note: "Profile" used to live in this bar but has been promoted to a
 * top-right avatar dropdown in the DriverDashboard header, freeing this
 * centre slot for "Messages". Drivers visit Messages many times per
 * session vs Profile maybe once — the centre slot now serves the more
 * frequent destination, with the unread badge in the strongest peripheral-
 * vision zone.
 */
const TABS = [
  { key: 'home',      label: 'Home',     Icon: Home },
  { key: 'jobs',      label: 'Jobs',     Icon: Package,        countKey: 'jobs',     needs: 'bidding' },
  { key: 'messages',  label: 'Messages', Icon: MessageSquare,  countKey: 'messages' },
  { key: 'my-work',   label: 'My Work',  Icon: Briefcase,      countKey: 'work',     needs: 'bidding' },
  { key: 'earnings',  label: 'Earnings', Icon: Wallet,         needs: 'bidding' },
  // shortLabel keeps the mobile bottom nav on ONE line even at 7 tabs.
  { key: 'profile',   label: 'Bidding Profile', shortLabel: 'Bidding', Icon: User,   needs: 'bidding' },
  { key: 'directory', label: 'Public Profile',  shortLabel: 'Public',  Icon: Globe,  countKey: 'enquiries', needs: 'directory' },
];

export const DriverNav = ({ active, onChange, counts = {}, useMode = 'both' }) => {
  // Filter tabs based on the driver's use-mode: directory-only drivers
  // don't see Jobs/My Work/Earnings; bidding-only drivers don't see
  // Public Profile. 'both' shows everything (legacy default).
  const tabs = TABS.filter((t) => {
    if (t.key === 'directory' && !DIRECTORY_ENABLED) return false;
    if (!t.needs) return true;
    if (useMode === 'both') return true;
    return useMode === t.needs;
  });
  // Messages uses a punchy red badge so unread chats are unmistakable.
  const Badge = ({ n, urgent }) => (
    n != null && n > 0 ? (
      <span className={`absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center leading-none ring-2 ring-white ${
        urgent ? 'bg-red-500' : 'bg-[#2E7D32]'
      }`}>
        {n > 99 ? '99+' : n}
      </span>
    ) : null
  );

  return (
    <>
      {/* Desktop / tablet: top pill bar */}
      <nav
        className="hidden md:flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-1.5 mb-6 shadow-sm sticky top-2 z-30"
        data-testid="driver-nav-desktop"
      >
        {tabs.map(({ key, label, Icon, countKey }) => {
          const isActive = active === key;
          const n = countKey ? counts[countKey] : undefined;
          const urgent = countKey === 'messages';
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={`relative flex items-center gap-2 px-4 lg:px-5 h-10 rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[#2E7D32] text-white shadow'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid={`driver-nav-${key}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span>{label}</span>
              {n != null && n > 0 && (
                <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : urgent
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                }`}>{n > 99 ? '99+' : n}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Mobile: bottom tab bar — 5 tabs */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        data-testid="driver-nav-mobile"
      >
        <ul className={
          tabs.length <= 3 ? 'grid grid-cols-3'
          : tabs.length === 4 ? 'grid grid-cols-4'
          : tabs.length === 5 ? 'grid grid-cols-5'
          : tabs.length === 6 ? 'grid grid-cols-6'
          : 'grid grid-cols-7'
        }>
          {tabs.map(({ key, label, shortLabel, Icon, countKey }) => {
            const isActive = active === key;
            const n = countKey ? counts[countKey] : undefined;
            const urgent = countKey === 'messages';
            // Use the shorter label on mobile so 7 tabs stay on one row.
            const mobileLabel = shortLabel || label;
            return (
              <li key={key} className="flex min-w-0">
                <button
                  type="button"
                  onClick={() => onChange(key)}
                  className={`relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-2 px-1 min-h-[58px] ${
                    isActive ? 'text-[#2E7D32]' : 'text-gray-500'
                  }`}
                  data-testid={`driver-nav-mobile-${key}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="relative">
                    <Icon className="w-[20px] h-[20px]" strokeWidth={isActive ? 2.5 : 1.8} />
                    <Badge n={n} urgent={urgent} />
                  </span>
                  <span className={`text-[10px] leading-tight text-center truncate max-w-full ${isActive ? 'font-bold' : 'font-medium'}`}>{mobileLabel}</span>
                  {isActive && <span className="absolute top-0 inset-x-4 h-[3px] bg-[#2E7D32] rounded-b-full" />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default DriverNav;
